import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Table from '../../components/Table/Table';
import AddButton from '../../components/Button/AddButton';
import { useTranslation } from 'react-i18next';
import { ALBUM_TABLE } from '../../utils/constants';
import { Community, LinkRow, Row } from '../../types';
import AlertModal from '../../components/AlertModal';

const AlbumScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    albumStore: { albums, getAllAlbums, deleteAlbum },
    userStore: { communities, user, getCommunities },
  } = useMst();
  const [selectedRow, setSelectedRow] = useState<Row | LinkRow>();
  const [result, setResult] = useState<Row[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const filterAlbums = useCallback(
    (communities: Community[], ownCommunities: Community[]) => {
      const filtered = communities.filter((community) => {
        return ownCommunities.some((el) => {
          return el.id === community.id;
        });
      });
      const filteredAlbums = albums.filter((album) => {
        return filtered.find(({ community }) => community === album.publisher);
      });
      return filteredAlbums;
    },
    [albums]
  );

  useEffect(() => {
    const getAlbums = async () => {
      await getAllAlbums();

      if (user?.admin) {
        const tempResult: Row[] =
          albums.length > 0
            ? albums.map((album) => ({
                id: album.id,
                name: album.name,
                value: `${album.name} - ${album.artist}`,
                secondValue: album.published ? t('published') : t('draft'),
              }))
            : [];
        setResult(tempResult);
      } else {
        if (communities && user?.communities.length) {
          const filtered = filterAlbums(communities, user.communities);
          const tempResult: Row[] =
            filtered && filtered.length > 0
              ? filtered.map((album) => ({
                  id: album.id,
                  name: album.name,
                  value: `${album.name} - ${album.artist}`,
                  secondValue: album.published ? t('published') : t('draft'),
                }))
              : [];
          setResult(tempResult);
        }
      }
    };

    getAlbums();
  }, [getAllAlbums, getCommunities, user, communities, albums, t, filterAlbums]);

  useEffect(() => {
    if (!selectedRow?.id || modalOpen) return;
    navigate(`/albums/${selectedRow.id}`);
  }, [selectedRow?.id, modalOpen, navigate]);

  const navigateTo = () => {
    navigate('/albums/create');
  };

  const deleteBook = async () => {
    if (selectedRow?.id) {
      await deleteAlbum(selectedRow.id.toString());
      await getAllAlbums();
      const tempResult: Row[] =
        albums.length > 0
          ? albums.map((album) => ({
              id: album.id,
              name: album.name,
              value: `${album.name} - ${album.artist}`,
              secondValue: album.published ? t('published') : t('draft'),
            }))
          : [];
      setResult(tempResult);
      setSelectedRow(undefined);
    }
    setModalOpen(false);
  };

  const toggleDelete = (id: number) => {
    setModalOpen(true);
    setSelectedRow({ id: id });
  };

  const cancel = () => {
    setSelectedRow(undefined);
    setModalOpen(false);
  };

  return (
    <Layout title={t('albums')}>
      <AddButton onClick={navigateTo} text={t('albums_add_btn')} />
      <Table
        tableType={ALBUM_TABLE}
        data={result}
        setSelectedRow={(value) => setSelectedRow({ id: value?.id, value: value?.value })}
        selectedRow={selectedRow}
        onDelete={(id) => toggleDelete(id)}
      />
      <AlertModal
        open={modalOpen}
        title="albums_delete"
        onClose={() => setModalOpen(!modalOpen)}
        leftButtonPress={cancel}
        rightButtonPress={deleteBook}
      />
    </Layout>
  );
});

export default AlbumScreen;
